import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Heading,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Section,
	StyleProps,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon, IconContainer } from "../../../../Components/Icon";
import {
	Guardian,
	GuardianNameFragment,
	Student,
	StudentNameAndImageFragment,
	StudentNameFragment,
} from "../../../../Utilities";
import { RelationshipDeleteButton } from "../../../DeleteButtons";
import { RelationshipCustomForm } from "../../../Forms";
import * as RelationshipComponents from "../../Relationship";
import { StudentIconNameDescription } from "../General";
import styles from "./styles.module.css";

export type StudentRelationshipModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	relationship: RelationshipComponents.CardRelationship & {
		student: Pick<Student, "id" | "graduationYear" | "yearsFromGraduation"> & StudentNameAndImageFragment;
	};
	guardian: Pick<Guardian, "id"> & GuardianNameFragment;
} & Partial<ClassNameProps & StyleProps>;

export function StudentRelationshipModal({
	relationship: { student, ...relationship },
	guardian,
	ifRef,
	onClose,
	className,
	style,
}: StudentRelationshipModalProps) {
	const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Modal
				ifRef={ifRef}
				onClose={onClose}
				className={makeClassName(styles["student-relationship-modal"], className)}
				style={style}
			>
				<Modal.Header>
					<StudentIconNameDescription student={student} size="large" />
				</Modal.Header>
				<Modal.Body>
					<Column justify="spaced-start">
						<Section>
							<Section.Header>
								<Row justify="space-between" align="center">
									<Row justify="spaced-start" align="center">
										<IconContainer color="var(--theme-colors__blue--light)">
											<Icon.Users color="var(--theme-colors__blue)" size={18} />
										</IconContainer>

										<Heading.H3 noMargin>Relationship Details</Heading.H3>
									</Row>

									<Button variant="link" size="small" onClick={openEditModal} className="button--icon">
										<Icon.Gear size={16} className="block" />
									</Button>
								</Row>
							</Section.Header>
							<Section.Body>
								<RelationshipComponents.RelationshipCard
									relationship={relationship}
									className={styles["student-relationship-modal__relationship-card"]}
								/>
							</Section.Body>
						</Section>
					</Column>
				</Modal.Body>
			</Modal>

			<Modal.If condition={isEditModalOpen}>
				<EditModal relationship={relationship} guardian={guardian} student={student} onClose={closeEditModal} />
			</Modal.If>
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	relationship: Omit<RelationshipCustomForm.ControlledDetailProps["relationship"], "guardian" | "student">;
	guardian: Pick<Guardian, "id"> & GuardianNameFragment;
	student: Pick<Student, "id"> & StudentNameFragment;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function EditModal({ relationship, guardian, student, ifRef, onClose, className, style }: EditModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	const relationshipWithPeople = React.useMemo(
		() => ({
			...relationship,
			guardian,
			student,
		}),
		[relationship, guardian, student],
	);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["edit-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Relationship</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					<RelationshipCustomForm.ControlledDetail
						relationship={relationshipWithPeople}
						formStateArgs={editFormStateArgs}
						noChangesDialog
						onSuccess={onSuccess}
					/>

					<RelationshipComponents.RelationshipDeletePermissionRequired>
						<RelationshipDeleteButton instanceID={relationship.id} size="small" onSuccess={onSuccess} />
					</RelationshipComponents.RelationshipDeletePermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
